import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import SectionCard from "../../components/catalog/SectionCard";
import { useCategories } from "../../hooks/useCategories";
import { useSections } from "../../hooks/useSections";

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="card-soft overflow-hidden animate-pulse"
        >
          <div className="aspect-[4/3] bg-[#FFE7D2]" />
          <div className="p-5">
            <div className="h-7 rounded bg-[#FFE7D2] w-2/3" />
            <div className="h-4 rounded bg-[#FFF0E3] mt-3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const {
    categories,
    categoriesLoading,
    categoriesError,
  } = useCategories();

  const {
    sections,
    sectionsLoading,
    sectionsError,
  } = useSections(categoryId);

  const category = useMemo(
    () => categories.find((item) => item.id === categoryId),
    [categories, categoryId]
  );

  const loading = categoriesLoading || sectionsLoading;
  const error = categoriesError || sectionsError;

  const handleSectionClick = (section) => {
    if (String(section.name || "").trim().toLowerCase() === "alterations") {
      navigate("/services/alterations");
      return;
    }
    navigate(`/category/${categoryId}/section/${section.id}`);
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />

      <main className="pt-28 pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524] hover:opacity-80"
          >
            <ArrowLeft size={16} />
            Back to home
          </button>

          <div className="mt-8 max-w-3xl">
            <div className="font-label text-[#F46524] mb-4">
              Explore Sections
            </div>

            <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#2B2B2B]">
              {category?.name || "StichB Services"}
            </h1>

            <p className="text-[#5b5a56] mt-5 text-lg leading-relaxed">
              {category?.description ||
                "Choose a section to explore the available services and products."}
            </p>
          </div>

          <div className="mt-14">
            {loading ? (
              <LoadingGrid />
            ) : error ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  Sections could not be loaded
                </h2>
                <p className="text-[#5b5a56] mt-2">
                  Please refresh the page and try again.
                </p>
              </div>
            ) : sections.length === 0 ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  No sections available yet
                </h2>
                <p className="text-[#5b5a56] mt-2">
                  New sections will appear here after they are added in the admin panel.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sections.map((section) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    onClick={handleSectionClick}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
